import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Pencil, 
  Trash2, 
  User, 
  Calendar, 
  Clock, 
  FileText, 
  Sparkles, 
  Copy 
} from 'lucide-react';
import { smeService } from '../services/api';
import { getCategoryDisplayName, getSubCategoryDisplayName, getTypeDisplayName } from '../constants/smeCategories';
import { useBrandedDialog } from './BrandedDialog';
import './SMEDetail.css';

const SMEDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { alert, confirm } = useBrandedDialog();
  const [sme, setSme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchSME = async () => {
      try {
        setLoading(true);
        const response = await smeService.getById(id);
        setSme(response.data);
        setError(null);
      } catch (err) {
        setError('Failed to load SME details');
        console.error('Error fetching SME:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSME();
  }, [id]);

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete SME?',
      message: `Delete "${sme.name}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      await smeService.delete(id);
      navigate('/');
    } catch (err) {
      alert({ title: 'Failed to delete SME', message: err.response?.data?.error || err.message || 'Please try again.', tone: 'danger' });
      console.error('Error deleting SME:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (name) => {
    if (!name) return 'SME';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const formatAuditUser = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.userName || [value.firstName, value.lastName].filter(Boolean).join(' ') || value.name || value._id || '';
  };

  const handleCopy = () => {
    if (sme?.prompt) {
      navigator.clipboard.writeText(sme.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="app-loading-container">
        <div className="app-loading-spinner" />
        <p>Loading SME details...</p>
      </div>
    );
  }

  if (error || !sme) {
    return (
      <div className="error">
        {error || 'SME not found'}
        <button className="btn btn-primary" onClick={() => navigate('/')} style={{ marginTop: '1rem' }}>
          Back to List
        </button>
      </div>
    );
  }

  // Fallback values if dates are missing, for styling demonstration
  const createdDate = sme.createdDate ? formatDate(sme.createdDate) : 'Unknown';
  // Assuming updatedAt exists, fallback to createdDate
  const updatedDate = sme.updatedAt ? formatDate(sme.updatedAt) : createdDate;

  return (
    <div className="sme-detail-page">
      <div className="sme-detail-main">
        {/* Top Actions */}
        <div className="smedetail-top-actions">
          <button className="smedetail-btn-back" onClick={() => navigate('/')}>
            <ArrowLeft size={16} strokeWidth={2} />
            Back to List
          </button>
          
          <div className="smedetail-top-actions-right">
            <button className="smedetail-btn-edit" onClick={() => navigate(`/sme/${id}/edit`)}>
              <Pencil size={14} strokeWidth={2} />
              Edit
            </button>
            <button className="smedetail-btn-delete" onClick={handleDelete}>
              <Trash2 size={14} strokeWidth={2} />
              Delete
            </button>
          </div>
        </div>

        {/* Profile Header */}
        <div className="smedetail-profile-header">
          <div className="smedetail-avatar">
            {getInitials(sme.name)}
          </div>
          <div className="smedetail-profile-info">
            <div className="smedetail-name-row">
              <h1 className="smedetail-name">{sme.name}</h1>
              <div className={`smedetail-status-badge ${sme.isActive === false ? 'inactive' : ''}`}>
                <span className="smedetail-status-dot"></span>
                {sme.isActive !== false ? 'Active' : 'Inactive'}
              </div>
            </div>
            <div className="smedetail-badges-row">
              <span className="smedetail-badge type-badge">
                {getTypeDisplayName(sme.type).toUpperCase()}
              </span>
              <span className="smedetail-badge category-badge">
                {getCategoryDisplayName(sme.smeCategory).toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        <div className="smedetail-divider"></div>

        {/* Stats Grid */}
        <div className="smedetail-stats-grid">
          <div className="smedetail-stat-card">
            <div className="smedetail-stat-icon-wrap" style={{ color: '#6366f1', background: '#e0e7ff' }}>
              <User size={18} strokeWidth={2} />
            </div>
            <div className="smedetail-stat-content">
              <p className="smedetail-stat-label">SPECIALITY</p>
              <p className="smedetail-stat-value">{getSubCategoryDisplayName(sme.subCategory) || 'N/A'}</p>
            </div>
          </div>
          <div className="smedetail-stat-card">
            <div className="smedetail-stat-icon-wrap" style={{ color: '#8b5cf6', background: '#ede9fe' }}>
              <Calendar size={18} strokeWidth={2} />
            </div>
            <div className="smedetail-stat-content">
              <p className="smedetail-stat-label">CREATED DATE</p>
              <p className="smedetail-stat-value">{createdDate}</p>
              {formatAuditUser(sme.createdBy) && (
                <p className="smedetail-stat-subvalue">By {formatAuditUser(sme.createdBy)}</p>
              )}
            </div>
          </div>
          <div className="smedetail-stat-card">
            <div className="smedetail-stat-icon-wrap" style={{ color: '#8b5cf6', background: '#ede9fe' }}>
              <Clock size={18} strokeWidth={2} />
            </div>
            <div className="smedetail-stat-content">
              <p className="smedetail-stat-label">LAST UPDATED</p>
              <p className="smedetail-stat-value">{updatedDate}</p>
              {formatAuditUser(sme.updatedBy) && (
                <p className="smedetail-stat-subvalue">By {formatAuditUser(sme.updatedBy)}</p>
              )}
            </div>
          </div>
        </div>

        {/* Description Card */}
        <div className="smedetail-full-card">
          <div className="smedetail-card-header">
            <div className="smedetail-card-title-group">
              <div className="smedetail-card-icon-wrap" style={{ color: '#8b5cf6', background: '#ede9fe' }}>
                <FileText size={18} strokeWidth={2} />
              </div>
              <h2 className="smedetail-card-title">DESCRIPTION</h2>
            </div>
          </div>
          <div className="smedetail-card-body">
            <p className="smedetail-description-text">
              {sme.description || 'No description provided.'}
            </p>
          </div>
        </div>

        {/* AI Prompt Card */}
        <div className="smedetail-full-card">
          <div className="smedetail-card-header">
            <div className="smedetail-card-title-group">
              <div className="smedetail-card-icon-wrap" style={{ color: '#a855f7', background: '#f3e8ff' }}>
                <Sparkles size={18} strokeWidth={2} />
              </div>
              <h2 className="smedetail-card-title">AI PROMPT (CHARACTER DEFINITION)</h2>
            </div>
            <button className="smedetail-btn-copy" onClick={handleCopy} title="Copy prompt">
              {copied ? <span style={{fontSize:'0.75rem', color:'#10b981', fontWeight:'600'}}>Copied!</span> : <Copy size={16} strokeWidth={2} />}
            </button>
          </div>
          <div className="smedetail-card-body smedetail-prompt-body">
            <div className="smedetail-prompt-box">
              {sme.prompt}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SMEDetail;
